import React from "react";

const STATUS_STYLE = {
  HEALTHY: "bg-emerald-50 text-emerald-700 border-emerald-200",
  WARNING: "bg-amber-50 text-amber-700 border-amber-200",
  CRITICAL: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_LABEL = { HEALTHY: "ZDRAVO", WARNING: "UPOZORENJE", CRITICAL: "KRITIČNO" };
const ms = (v) => (v === null || v === undefined ? "—" : `${v} ms`);
const pctv = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(2)}%`);
const time = (s) => (s ? new Date(s).toLocaleString("sr-RS", { dateStyle: "short", timeStyle: "medium" }) : "—");

function StatusCard({ c }) {
  return (
    <div className={`rounded-2xl border p-3 ${STATUS_STYLE[c.status] || STATUS_STYLE.HEALTHY}`}
      data-testid={`tech-card-${c.key}`}>
      <p className="text-[10px] font-black uppercase tracking-wider opacity-70">{c.label}</p>
      <p className="font-display font-black text-xl mt-0.5">
        {c.value ?? "—"} <span className="text-xs font-bold">{c.unit}</span>
      </p>
      <p className="text-[11px] font-black mt-0.5">{STATUS_LABEL[c.status] || c.status}</p>
      <p className="text-[11px] opacity-80 mt-0.5">{c.reason}</p>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1 mt-1">{title}</h3>
      {children}
    </div>
  );
}

function Grid({ cols, rows, testid, keyOf }) {
  if (!rows?.length) return <p data-testid={testid} className="bg-white rounded-2xl border p-3 text-sm text-slate-400">Nema zapisa u izabranom prozoru.</p>;
  return (
    <div className="bg-white rounded-2xl border overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
          {cols.map((c) => <th key={c.key} className="p-2.5 font-black">{c.label}</th>)}
        </tr></thead>
        <tbody className="divide-y">
          {rows.map((r, i) => (
            <tr key={keyOf ? keyOf(r) : i}>
              {cols.map((c) => <td key={c.key} className="p-2.5">{c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TechAnalytics({ d, mode, onMode }) {
  const h = d.health;
  const v = d.web_vitals.metrics;
  return (
    <div className="space-y-3" data-testid="tech-analytics">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-black border ${STATUS_STYLE[h.overall]}`} data-testid="tech-overall">
          TEHNIČKO STANJE: {STATUS_LABEL[h.overall]}
        </span>
        <span className="text-[11px] text-slate-400">Trenutno = kotrljajući prozor {h.window_minutes} min</span>
        {[["range", "Istorija (filter)"], ["60m", "60 min"], ["24h", "24h"]].map(([k, l]) => (
          <button key={k} data-testid={`tech-window-${k}`} onClick={() => onMode(k)}
            className={`px-3 py-1 rounded-full text-[11px] font-bold ${mode === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
        <span className="text-[11px] text-slate-400">Okruženje: {d.environment} · sirovi zapisi {d.retention_days} dana</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="tech-status-cards">
        {h.cards.map((c) => <StatusCard key={c.key} c={c} />)}
      </div>
      <p className="text-[11px] text-slate-400">{h.note}</p>

      <Section title="API performanse — kritični tokovi" testid="tech-flows">
        <Grid testid="tech-by-flow" keyOf={(r) => r.flow} rows={d.api.by_flow} cols={[
          { key: "flow", label: "Tok" }, { key: "requests", label: "Zahteva" },
          { key: "count_4xx", label: "4xx" }, { key: "count_5xx", label: "5xx" },
          { key: "error_rate_5xx", label: "5xx stopa", fmt: pctv },
          { key: "p50_ms", label: "P50", fmt: ms }, { key: "p75_ms", label: "P75", fmt: ms },
          { key: "p95_ms", label: "P95", fmt: ms }]} />
      </Section>

      <Section title="Najvažnije rute" testid="tech-routes">
        <Grid testid="tech-by-route" keyOf={(r) => r.route} rows={d.api.by_route} cols={[
          { key: "route", label: "Ruta" }, { key: "requests", label: "Zahteva" },
          { key: "success_rate", label: "Uspešnost", fmt: pctv },
          { key: "count_4xx", label: "4xx" }, { key: "count_5xx", label: "5xx" },
          { key: "p50_ms", label: "P50", fmt: ms }, { key: "p95_ms", label: "P95", fmt: ms }]} />
        <p className="text-[11px] text-slate-400 mt-1">{d.api.note}</p>
      </Section>

      <Section title="Grupisane greške" testid="tech-errors">
        <Grid testid="tech-error-groups" keyOf={(r) => r.fingerprint} rows={d.errors.groups} cols={[
          { key: "kind", label: "Tip" }, { key: "route", label: "Endpoint / ruta" },
          { key: "category", label: "Kategorija" }, { key: "error_code", label: "Kod" },
          { key: "occurrences", label: "Pojava" },
          { key: "affected_sessions", label: "Sesija", fmt: (x) => x ?? "—" },
          { key: "first_seen", label: "Prvi put", fmt: time },
          { key: "last_seen", label: "Poslednji put", fmt: time }]} />
        <p className="text-[11px] text-slate-400 mt-1">{d.errors.note}</p>
      </Section>

      <Section title="Web performanse (Core Web Vitals)" testid="tech-vitals">
        <Grid testid="tech-vitals-table" keyOf={(r) => r.metric} cols={[
          { key: "metric", label: "Metrika" }, { key: "samples", label: "Uzoraka" },
          { key: "p50", label: "P50" }, { key: "p75", label: "P75" }, { key: "p95", label: "P95" },
          { key: "ratings", label: "Ocene", fmt: (r) => Object.entries(r || {}).map(([k, n]) => `${k}: ${n}`).join(" · ") || "—" }]}
          rows={Object.entries(v).map(([k, x]) => ({ metric: k, ...x }))} />
        <p className="text-[11px] text-slate-400 mt-1">{d.web_vitals.note}</p>
      </Section>

      <Section title="Frontend greške i neuspešni API pozivi" testid="tech-frontend">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white rounded-2xl border p-3" data-testid="tech-fe-errors">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Frontend greške</p>
            <p className="font-display font-black text-xl">{d.frontend.frontend_errors}</p>
          </div>
          <div className="bg-white rounded-2xl border p-3" data-testid="tech-fe-api-fails">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Neuspešni API pozivi</p>
            <p className="font-display font-black text-xl">{d.frontend.failed_api_requests}</p>
          </div>
        </div>
        <Grid testid="tech-fe-by-route" keyOf={(r) => r.key} rows={d.frontend.errors_by_route}
          cols={[{ key: "key", label: "Ruta" }, { key: "count", label: "Grešaka" }]} />
      </Section>

      <Section title="Realtime i eksterni servisi" testid="tech-realtime">
        <div className="bg-white rounded-2xl border divide-y text-sm" data-testid="tech-realtime-block">
          <div className="p-3 flex justify-between"><span className="text-slate-500">Aktivne konekcije</span><b>{d.realtime.active_connections}</b></div>
          <div className="p-3 flex justify-between"><span className="text-slate-500">Aktivne porudžbine</span><b>{d.realtime.active_orders}</b></div>
          <div className="p-3 flex justify-between"><span className="text-slate-500">Prekidi (60 min)</span><b>{d.realtime.disconnects_last_60_min}</b></div>
          <p className="p-3 text-[11px] text-slate-400">{d.realtime.reason}</p>
        </div>
        <Grid testid="tech-upstream" keyOf={(r) => r.service} cols={[
          { key: "service", label: "Servis" }, { key: "attempts", label: "Pokušaja" },
          { key: "failures", label: "Grešaka" }, { key: "failure_rate", label: "Stopa", fmt: pctv }]}
          rows={[{ service: "Email (Resend)", ...d.upstream.EMAIL }, { service: "Geolokacija", ...d.upstream.GEO }]} />
        <p className="text-[11px] text-slate-400 mt-1">{d.upstream.note}</p>
      </Section>

      <Section title="Istorijski trend (dnevni agregati)" testid="tech-trend">
        <Grid testid="tech-daily-trend" keyOf={(r) => r.day} rows={d.daily_trend} cols={[
          { key: "day", label: "Dan" }, { key: "requests", label: "Zahteva" },
          { key: "count_4xx", label: "4xx" }, { key: "count_5xx", label: "5xx" },
          { key: "error_rate_5xx", label: "5xx stopa", fmt: pctv },
          { key: "p95_ms_approx", label: "P95 (pribl.)", fmt: ms }]} />
      </Section>

      <p className="text-[11px] text-slate-400" data-testid="tech-privacy">{d.privacy_note}</p>
      <p className="text-[11px] text-slate-400" data-testid="tech-alerting">{d.alerting_note}</p>
      <p className="text-[11px] text-slate-400" data-testid="tech-data-quality">
        Kvalitet podataka — nevalidni zapisi: {d.data_quality.invalid_or_missing_duration}. {d.data_quality.note}
      </p>
    </div>
  );
}
